/**
 * Wealth Check — copy, option lists, and deterministic scoring engine.
 *
 * Ported 1:1 from the marketing-provided static prototype
 * (wealth-check-prototype-A-editorial.html). The scoring logic below (buildReflection /
 * buildSpots) mirrors that prototype's inline <script> exactly — same pool, same
 * severities, same top-3-by-severity selection — just typed and split out of the
 * component. Do not "improve" the copy or weights here without checking back with
 * the prototype; this is a faithful port, not a redesign.
 */

import type { InvestableAssetsRange } from '@nwwp/database'

export type SingleOption = { value: string; label: string }

export const AGE_OPTIONS: SingleOption[] = [
  { value: 'under30', label: 'Under 30' },
  { value: '30s', label: 'In my 30s' },
  { value: '40s', label: 'In my 40s' },
  { value: '50plus', label: '50 or older' },
]

export const INCOME_OPTIONS: SingleOption[] = [
  { value: 'under100', label: 'Below $100K' },
  { value: '100to250', label: '$100K–$250K' },
  { value: '250', label: '$250K–$500K' },
  { value: '500', label: '$500K+' },
]

export const SAVINGS_OPTIONS: SingleOption[] = [
  { value: 'low', label: 'Under 5%' },
  { value: 'mid', label: '5–15%' },
  { value: 'good', label: '15–25%' },
  { value: 'high', label: '25%+' },
]

export const EMERGENCY_OPTIONS: SingleOption[] = [
  { value: 'thin', label: 'Under 1 month' },
  { value: 'some', label: '1–3 months' },
  { value: 'ok', label: '3–6 months' },
  { value: 'strong', label: '6+ months' },
]

export const ASSET_OPTIONS: SingleOption[] = [
  { value: 'under100k', label: 'Under $100K' },
  { value: '100to250k', label: '$100K–$250K' },
  { value: '250to500k', label: '$250K–$500K' },
  { value: '500kto1m', label: '$500K–$1M' },
  { value: 'over1m', label: '$1M+' },
]

export const MATCH_OPTIONS: SingleOption[] = [
  { value: 'match', label: 'Contributing up to the employer match' },
  { value: 'max', label: 'Maxing it out' },
  { value: 'none', label: 'No plan / no match' },
]

export const EQUITY_OPTIONS: SingleOption[] = [
  { value: 'rsu', label: 'RSUs' },
  { value: 'options', label: 'Stock options' },
  { value: 'espp', label: 'ESPP' },
  { value: 'private', label: 'Private-company shares' },
  { value: 'none', label: 'None' },
]

export const CONCENTRATION_OPTIONS: SingleOption[] = [
  { value: 'low', label: 'Under 10%' },
  { value: 'mid', label: '10–25%' },
  { value: 'high', label: '25–50%' },
  { value: 'veryhigh', label: 'Over half' },
  { value: 'unsure', label: 'Not sure' },
]

export const IPO_OPTIONS: SingleOption[] = [
  { value: 'expected', label: 'Yes, expected' },
  { value: 'public', label: 'Already public' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Not sure' },
]

export const SELLING_OPTIONS: SingleOption[] = [
  { value: 'schedule', label: 'Selling on a schedule' },
  { value: 'adhoc', label: 'Selling ad hoc' },
  { value: 'accumulate', label: 'Just accumulating' },
]

export type Answers = {
  age?: string
  income?: string
  savings?: string
  emergency?: string
  assets?: string
  match?: string
  equity: string[]
  concentration?: string
  ipo?: string
  selling?: string
}

export const EMPTY_ANSWERS: Answers = { equity: [] }

/** Mirrors the prototype's `hasEquity()` — selections exist and aren't just "none". */
export function hasEquity(answers: Answers): boolean {
  return answers.equity.length > 0 && !(answers.equity.length === 1 && answers.equity[0] === 'none')
}

/** Equity follow-up questions are required when an equity type is selected. */
export function equityFollowUpsComplete(answers: Answers): boolean {
  return !hasEquity(answers) || Boolean(answers.concentration && answers.ipo && answers.selling)
}

/** Results remain visible when browser history restores the post-unlock step. */
export function shouldShowResults(step: number, resultsUnlocked: boolean): boolean {
  return step === 5 || (step === 4 && resultsUnlocked)
}

/** Mirrors the prototype's `toggleAdvisorCta()` qualification check. */
export function qualifiesForAdvisor(answers: Answers): boolean {
  return Boolean(answers.income) && answers.income !== 'under100'
}

const AGE_WORD: Record<string, string> = {
  under30: 'in your late twenties',
  '30s': 'in your thirties',
  '40s': 'in your forties',
  '50plus': 'in your fifties or beyond',
}

// Ranks preserve the original dollar thresholds after merging the old $100k–$150k and
// $150k–$250k buckets into one $100K–$250K bucket, so `incRank >= 3` still means $250K+.
const INCOME_RANK: Record<string, number> = { under100: 0, '100to250': 2, '250': 3, '500': 4 }
const ASSET_RANK: Record<string, number> = { under100k: 0, '100to250k': 1, '250to500k': 2, '500kto1m': 3, over1m: 4 }

function incomeRank(answers: Answers): number {
  return INCOME_RANK[answers.income ?? ''] ?? 0
}

function assetRank(answers: Answers): number {
  return ASSET_RANK[answers.assets ?? ''] ?? 0
}

/** Mirrors the prototype's `buildReflection()`. Returns the quoted reflection sentence (no quote marks). */
export function buildReflection(answers: Answers): string {
  const incRank = incomeRank(answers)
  const assRank = assetRank(answers)

  const parts: string[] = []
  const earner = incRank >= 3 ? 'a high earner' : 'building momentum'
  parts.push(`You're ${earner} ${AGE_WORD[answers.age ?? ''] ?? 'mid-career'}`)
  if (hasEquity(answers)) parts.push('with equity comp in the mix')

  let tail: string
  if (hasEquity(answers) && (answers.concentration === 'veryhigh' || answers.concentration === 'high')) {
    tail = " — and a real chunk of your future is riding on one company's stock."
  } else if (answers.ipo === 'expected') {
    tail = ' — with a possible liquidity event that could change the math fast.'
  } else if (incRank >= 3 && assRank <= 0) {
    tail = ' — earning well, with room to turn that income into lasting wealth.'
  } else if (answers.match === 'max') {
    tail = ' — doing a lot right, with a few refinements left on the table.'
  } else {
    tail = ' — juggling a lot of moving parts at once.'
  }

  const sentence = parts.join(', ') + tail
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

export type Spot = { sev: number; tag: string; h: string; p: string }

/** Mirrors the prototype's `buildSpots()` — builds the full candidate pool, then takes the top 3 by severity. */
export function buildSpots(answers: Answers): Spot[] {
  const incRank = incomeRank(answers)
  const assRank = assetRank(answers)
  const pool: Spot[] = []

  if (answers.concentration === 'veryhigh') {
    pool.push({
      sev: 10,
      tag: 'Concentration',
      h: 'A lot rides on one company',
      p: "More than half your net worth sitting in your employer's stock is a level of concentration risk most people wouldn't choose on purpose. Think through how much exposure you actually want to have and consider a diversified portfolio for the rest.",
    })
  } else if (answers.concentration === 'high') {
    pool.push({
      sev: 7,
      tag: 'Concentration',
      h: 'One stock is carrying a lot',
      p: 'A quarter to half of your net worth tied to one employer means your job and your investments rise and fall together. Diversifying that over time is worth a conversation.',
    })
  }

  if (hasEquity(answers) && answers.selling === 'accumulate') {
    pool.push({
      sev: 8,
      tag: 'Equity',
      h: 'Vested shares with no selling plan',
      p: "Letting vested shares pile up without a plan quietly ties your finances to one stock's performance. Many people in this position consider a rules-based approach to selling to try to take the emotions out of it.",
    })
  }

  if (answers.ipo === 'expected') {
    pool.push({
      sev: 8,
      tag: 'IPO & tax',
      h: 'A liquidity event can surprise you at tax time',
      p: 'IPOs and other liquidity events often create a tax bill that lands the following spring and catches people off guard. Planning ahead of the event is far easier than reacting after it.',
    })
  }

  if (answers.match === 'none') {
    pool.push({
      sev: 9,
      tag: 'Free money',
      h: 'You may be skipping the employer match',
      p: "If there's a plan available, not capturing the full match is free money left on the table every paycheck.",
    })
  } else if (answers.match === 'match' && incRank >= 2) {
    pool.push({
      sev: 6,
      tag: 'Tax-advantaged room',
      h: "You're matching, but not maxing",
      p: 'At your income, stopping at the match usually leaves thousands of tax-advantaged dollars unused each year. Contributing closer to the annual max may be worth considering as part of your broader tax and retirement planning.',
    })
  }

  if (answers.emergency === 'thin') {
    pool.push({
      sev: 7,
      tag: 'Safety net',
      h: 'A thin cash cushion for your income',
      p: "Under a month of expenses on hand is a short runway, especially with variable income or equity comp. Building toward a few months' buffer lowers the stakes on every other decision.",
    })
  }

  if (answers.savings === 'low') {
    pool.push({
      sev: 6,
      tag: 'Savings rate',
      h: 'High income, modest savings',
      p: `Lifestyle tends to rise with income without anyone deciding it should. Naming a target savings rate turns "whatever's left" into a deliberate choice.`,
    })
  }

  if (incRank >= 3 && assRank <= 0) {
    pool.push({
      sev: 7,
      tag: 'Income vs. assets',
      h: "Your income hasn't become wealth yet",
      p: "Earning $250k+ with little invested outside retirement usually means strong cash flow that isn't being converted into lasting assets. Turning income into invested wealth is where a plan pays for itself fastest.",
    })
  }

  if (assRank >= 2 && answers.match === 'match') {
    pool.push({
      sev: 5,
      tag: 'Tax efficiency',
      h: 'A big taxable base, room in tax-advantaged accounts',
      p: 'Sizable assets outside retirement alongside an un-maxed plan often signals a tax mix worth rebalancing — shifting more into tax-advantaged space may improve after-tax outcomes depending on your circumstances.',
    })
  }

  if (answers.emergency === 'strong' && assRank <= 1 && incRank >= 2) {
    pool.push({
      sev: 5,
      tag: 'Idle cash',
      h: 'You may be holding more cash than you need',
      p: 'A deep cash buffer is great, but past a point extra cash in the bank slowly loses ground to inflation. Some of it may be better put to work toward your goals.',
    })
  }

  pool.push({
    sev: 4,
    tag: 'Protection',
    h: 'The safety net most people outgrow',
    p: "As income and responsibilities grow, the life and disability coverage from work often quietly falls short of what you'd actually want in place. It's worth a periodic gut-check.",
  })
  pool.push({
    sev: 3,
    tag: 'Estate basics',
    h: "The paperwork that's easy to put off",
    p: 'Beneficiary designations, a will, and how accounts are titled rarely keep up with real life. A quick review makes sure the right things reach the right people.',
  })
  pool.push({
    sev: 3,
    tag: 'Investment drift',
    h: 'When did you last look under the hood?',
    p: 'Portfolios drift, fees quietly add up, and old 401(k)s get stranded at past employers. A periodic review keeps your investments aligned with your goals and risk.',
  })
  pool.push({
    sev: 2,
    tag: 'Fit',
    h: "Your pieces aren't talking to each other",
    p: 'Individually your finances may look fine, but savings, investments, taxes, and goals are usually managed in separate silos. The biggest gains often come from coordinating them.',
  })
  pool.push({
    sev: 1,
    tag: 'Intention',
    h: 'Is your money pointed where you want it?',
    p: 'Money is a means to a good life, not an end in itself. Tying each dollar to what you actually want makes trade-offs far easier to make with confidence.',
  })

  return [...pool].sort((a, b) => b.sev - a.sev).slice(0, 3)
}

export function wealthCheckResultPayload(answers: Answers, step: number, resultsUnlocked: boolean) {
  if (!shouldShowResults(step, resultsUnlocked)) return { reflection: '', spots: [] as Spot[] }
  return { reflection: buildReflection(answers), spots: buildSpots(answers) }
}

/**
 * Map the quiz's "invested assets outside retirement" bucket to the Salesforce
 * `investable_assets_range` (required on lead creation). The quiz buckets now line up
 * 1:1 with the Salesforce ranges.
 *
 * Product intent: the quiz asks about assets held outside retirement accounts, mapped
 * 1:1 to Salesforce ranges (same buckets). This can understate total investable assets
 * but matches the prototype and product spec for this page.
 */
export const ASSETS_TO_INVESTABLE_RANGE: Record<string, InvestableAssetsRange> = {
  under100k: 'UNDER_100K',
  '100to250k': 'FROM_100_TO_250K',
  '250to500k': 'FROM_250_TO_500K',
  '500kto1m': 'FROM_500K_TO_1M',
  over1m: 'OVER_1M',
}

/** Representative age (years) per age bucket — used as the LeadScorer `age` input. */
export const AGE_BUCKET_TO_YEARS: Record<string, number> = {
  under30: 28,
  '30s': 35,
  '40s': 45,
  // "50 or older" is open-ended — omit a numeric age at lead creation (see createWealthCheckLead).
}

/** Representative annual income (USD) per income bucket — LeadScorer `annual_income` input. */
export const INCOME_BUCKET_TO_ANNUAL: Record<string, number> = {
  under100: 75_000,
  '100to250': 175_000,
  '250': 375_000,
  '500': 600_000,
}

export type LabeledAnswer = { value: string; label: string }

export type LabeledAnswers = {
  age?: LabeledAnswer
  income?: LabeledAnswer
  savings?: LabeledAnswer
  emergency?: LabeledAnswer
  assets?: LabeledAnswer
  match?: LabeledAnswer
  equity: LabeledAnswer[]
  concentration?: LabeledAnswer
  ipo?: LabeledAnswer
  selling?: LabeledAnswer
}

function labeled(options: SingleOption[], value: string | undefined): LabeledAnswer | undefined {
  if (!value) return undefined
  const label = options.find((o) => o.value === value)?.label
  return label ? { value, label } : undefined
}

/** Quiz answers as `{ value, label }` so Customer.io Liquid can render copy without a lookup table. */
export function buildLabeledAnswers(answers: Answers): LabeledAnswers {
  return {
    age: labeled(AGE_OPTIONS, answers.age),
    income: labeled(INCOME_OPTIONS, answers.income),
    savings: labeled(SAVINGS_OPTIONS, answers.savings),
    emergency: labeled(EMERGENCY_OPTIONS, answers.emergency),
    assets: labeled(ASSET_OPTIONS, answers.assets),
    match: labeled(MATCH_OPTIONS, answers.match),
    equity: answers.equity.flatMap((value) => labeled(EQUITY_OPTIONS, value) ?? []),
    concentration: labeled(CONCENTRATION_OPTIONS, answers.concentration),
    ipo: labeled(IPO_OPTIONS, answers.ipo),
    selling: labeled(SELLING_OPTIONS, answers.selling),
  }
}

/**
 * Human-readable summary written to the Salesforce lead's Description so advisors have
 * the full context: the answers that don't get their own Salesforce field (age, income,
 * and investable-assets range are stored as fields separately) plus the computed results.
 */
export function buildLeadNotes(answers: Answers): string {
  const labelFor = (options: SingleOption[], value: string | undefined) =>
    options.find((o) => o.value === value)?.label ?? '—'

  const lines: string[] = ['Wealth Check — additional context', '']

  lines.push(`Savings rate: ${labelFor(SAVINGS_OPTIONS, answers.savings)}`)
  lines.push(`Emergency cash on hand: ${labelFor(EMERGENCY_OPTIONS, answers.emergency)}`)
  lines.push(`401(k): ${labelFor(MATCH_OPTIONS, answers.match)}`)

  const equity = answers.equity
    .map((v) => EQUITY_OPTIONS.find((o) => o.value === v)?.label)
    .filter((label): label is string => Boolean(label))
  lines.push(`Equity compensation: ${equity.length ? equity.join(', ') : '—'}`)

  if (hasEquity(answers)) {
    lines.push(`Employer-stock concentration: ${labelFor(CONCENTRATION_OPTIONS, answers.concentration)}`)
    lines.push(`Pre-IPO / liquidity event: ${labelFor(IPO_OPTIONS, answers.ipo)}`)
    lines.push(`Vested shares: ${labelFor(SELLING_OPTIONS, answers.selling)}`)
  }

  lines.push('', 'What we heard:', `“${buildReflection(answers)}”`, '', 'Top 3 things worth a look:')
  buildSpots(answers).forEach((spot, index) => {
    lines.push(`${index + 1}. [${spot.tag}] ${spot.h} — ${spot.p}`)
  })

  return lines.join('\n')
}
